import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Printer, FileText, Calendar, CreditCard, User, Building2, DollarSign } from "lucide-react";
import { Button } from "./Button";
import { Badge } from "./badge";
import { Card } from "./card";
import { Escrow, EscrowPaymentProvider } from "../../services/escrowAPI";
import { PlatformFeeUtils, PLATFORM_CONFIG } from "../../config/platformConfig";
import { jsPDF } from "jspdf";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

interface ReceiptDialogProps {
    isOpen: boolean;
    onClose: () => void;
    transaction: Escrow | null;
}

const ReceiptDialog: React.FC<ReceiptDialogProps> = ({ isOpen, onClose, transaction }) => {
    const { t } = useTranslation();
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    if (!transaction || !isOpen) return null;

    const getStatusBadge = (status: any) => {
        const statusName = typeof status === "string" ? status : status?.name || "unknown";
        const styles = {
            completed: "bg-green-100 text-green-700",
            paid: "bg-green-100 text-green-700",
            funded: "bg-green-100 text-green-700",
            pending: "bg-yellow-100 text-yellow-700",
            created: "bg-yellow-100 text-yellow-700",
            failed: "bg-red-100 text-red-700",
            cancelled: "bg-red-100 text-red-700",
            released: "bg-blue-100 text-blue-700"
        };
        return styles[statusName.toLowerCase()] || "bg-gray-100 text-gray-700";
    };

    const getStatusText = (status: any) => {
        const statusName = typeof status === "string" ? status : status?.name || "unknown";
        return statusName.charAt(0).toUpperCase() + statusName.slice(1).toLowerCase();
    };

    const getCurrencyDisplay = (currency: any) => {
        return getDynamicCurrencySymbol();
    };

    const generatePDF = async () => {
        if (!transaction) return;

        setIsGeneratingPDF(true);
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 20;
            let yPosition = 20;

            // Helper function to add text
            const addText = (text: string, x: number, y: number, options: any = {}) => {
                doc.setFontSize(options.fontSize || 12);
                doc.setTextColor(options.color || "#000000");
                doc.text(text, x, y);
            };

            // Helper function to add line
            const addLine = (y: number) => {
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, y, pageWidth - margin, y);
            };

            // Header
            addText(t("receiptDialog.kokobeo"), pageWidth / 2, yPosition, { fontSize: 24, color: "#2563eb" });
            yPosition += 10;
            addText(t("receiptDialog.transactionReceipt"), pageWidth / 2, yPosition, { fontSize: 16 });
            yPosition += 20;

            // Receipt ID and Date
            addText(`${t("receiptDialog.receiptId")} ${transaction.id}`, margin, yPosition);
            addText(`${new Date(transaction.createdAt).toLocaleDateString()}`, pageWidth - margin - 50, yPosition);
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Transaction Details
            addText(t("receiptDialog.transactionDetails"), margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            addText(`${t("receiptDialog.statusLabel")} ${getStatusText(transaction.status).toUpperCase()}`, margin, yPosition);
            yPosition += 8;

            addText(`${t("receiptDialog.descriptionLabel")} ${transaction.milestone?.description || t("receiptDialog.escrowPayment")}`, margin, yPosition);
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Parties Involved
            addText(t("receiptDialog.partiesInvolved"), margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            // Client
            addText(t("receiptDialog.clientLabel"), margin, yPosition);
            yPosition += 8;
            addText(`${transaction.client?.firstName} ${transaction.client?.lastName}`, margin + 10, yPosition);
            yPosition += 6;
            addText(transaction.client?.email || "N/A", margin + 10, yPosition);
            yPosition += 10;

            // Professional
            addText(t("receiptDialog.professionalLabel"), margin, yPosition);
            yPosition += 8;
            addText(`${transaction.professional?.firstName} ${transaction.professional?.lastName}`, margin + 10, yPosition);
            yPosition += 6;
            addText(transaction.professional?.email || "N/A", margin + 10, yPosition);
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Amount Breakdown
            addText(t("receiptDialog.amountBreakdown"), margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

            const milestoneAmount = transaction.amount || 0;
            const platformFee = transaction.platformFee || PlatformFeeUtils.calculatePlatformFee(milestoneAmount);
            const totalAmount = transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(milestoneAmount);

            addText("Milestone Amount:", margin, yPosition);
            addText(`${getCurrencyDisplay(transaction.currency)} ${milestoneAmount.toFixed(2)}`, pageWidth - margin - 50, yPosition);
            yPosition += 8;

            addText(`${PLATFORM_CONFIG.PLATFORM_FEE_LABEL} (${PlatformFeeUtils.getPlatformFeePercentageDisplay()}):`, margin, yPosition);
            addText(`${getCurrencyDisplay(transaction.currency)} ${platformFee.toFixed(2)}`, pageWidth - margin - 50, yPosition);
            yPosition += 8;

            addLine(yPosition);
            yPosition += 8;

            addText("TOTAL AMOUNT:", margin, yPosition, { fontSize: 14, color: "#374151" });
            addText(`${getCurrencyDisplay(transaction.currency)} ${totalAmount.toFixed(2)}`, pageWidth - margin - 50, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 15;

            addLine(yPosition);
            yPosition += 10;

            // Payment Information
            addText("PAYMENT INFORMATION", margin, yPosition, { fontSize: 14, color: "#374151" });
            yPosition += 10;

             addText(`Payment Method: ${transaction.paymentProvider === EscrowPaymentProvider.STRIPE ? "Stripe" : "Payment Gateway"}`, margin, yPosition);
            yPosition += 8;

            if (transaction.stripePaymentIntentId) {
                addText(`Payment ID: ${transaction.stripePaymentIntentId}`, margin, yPosition);
                yPosition += 8;
            }

            addText(`Created: ${new Date(transaction.createdAt).toLocaleString()}`, margin, yPosition);
            yPosition += 8;

            addText(`Updated: ${new Date(transaction.updatedAt).toLocaleString()}`, margin, yPosition);
            yPosition += 20;

            // Footer
            addLine(yPosition);
            yPosition += 10;
            addText(t("receiptDialog.thankYou"), pageWidth / 2, yPosition, { fontSize: 12, color: "#6b7280" });
            yPosition += 8;
            addText(t("receiptDialog.automatedReceipt"), pageWidth / 2, yPosition, { fontSize: 10, color: "#9ca3af" });

            // Save the PDF
            const fileName = `receipt-${transaction.id}-${new Date().toISOString().split("T")[0]}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error("Error generating PDF:", error);
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const printReceipt = () => {
        window.print();
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
                <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.95 }}
                    className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                >
                    {/* Header */}
                    <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex justify-between items-start">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <FileText className="h-6 w-6 text-blue-600" />
                                    <h2 className="text-2xl font-bold text-gray-900">{t("receiptDialog.title")}</h2>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Badge variant={undefined} className={getStatusBadge(transaction.status)}>
                                        {getStatusText(transaction.status).toUpperCase()}
                                    </Badge>
                                    <Badge variant="outline" className="border-green-200 text-green-700">
                                        {t("receiptDialog.escrow")}
                                    </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {t("receiptDialog.receiptNumber", { id: transaction.id, date: new Date(transaction.createdAt).toLocaleDateString() })}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Receipt Content */}
                    <div className="p-6 space-y-6" id="receipt-content">
                        {/* Company Header */}
                        <div className="text-center py-6 border-b">
                            <h1 className="text-3xl font-bold text-blue-600 mb-2">{t("receiptDialog.kokobeo")}</h1>
                            <p className="text-gray-600">{t("receiptDialog.transactionReceipt")}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleString()}
                            </p>
                        </div>

                        {/* Transaction Summary */}
                        <Card className="p-6 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">{t("receiptDialog.transactionInformation")}</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.receiptId")}</span>
                                            <span className="font-medium">{transaction.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.status")}</span>
                                            <Badge variant={undefined} className={getStatusBadge(transaction.status)}>
                                                {getStatusText(transaction.status)}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.description")}</span>
                                            <span className="font-medium text-right max-w-xs">
                                                {transaction.milestone?.description || t("receiptDialog.escrowPayment")}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">{t("receiptDialog.amountSummary")}</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.totalAmount")}</span>
                                            <span className="font-bold text-lg text-gray-900">
                                                {getCurrencyDisplay(transaction.currency)} {(transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.milestoneAmount")}</span>
                                            <span className="font-medium">
                                                {getCurrencyDisplay(transaction.currency)} {(transaction.amount || 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("receiptDialog.platformFee")}</span>
                                            <span className="font-medium">
                                                {getCurrencyDisplay(transaction.currency)} {(transaction.platformFee || PlatformFeeUtils.calculatePlatformFee(transaction.amount || 0)).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Parties Involved */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <User className="h-5 w-5 text-blue-600" />
                                    <h3 className="font-semibold text-gray-900">{t("receiptDialog.client")}</h3>
                                </div>
                                <div className="space-y-2">
                                    <p className="font-medium text-lg">
                                        {transaction.client?.firstName} {transaction.client?.lastName}
                                    </p>
                                    <p className="text-gray-600">{transaction.client?.email}</p>
                                    <Badge variant="secondary">{t("receiptDialog.client")}</Badge>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Building2 className="h-5 w-5 text-green-600" />
                                    <h3 className="font-semibold text-gray-900">{t("receiptDialog.professional")}</h3>
                                </div>
                                <div className="space-y-2">
                                    <p className="font-medium text-lg">
                                        {transaction.professional?.firstName} {transaction.professional?.lastName}
                                    </p>
                                    <p className="text-gray-600">{transaction.professional?.email}</p>
                                    <Badge variant="secondary">{t("receiptDialog.professional")}</Badge>
                                </div>
                            </Card>
                        </div>

                        {/* Payment Details */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <CreditCard className="h-5 w-5 text-purple-600" />
                                <h3 className="font-semibold text-gray-900">{t("receiptDialog.paymentDetails")}</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-gray-600">{t("receiptDialog.paymentMethod")}</span>
                                     <p className="font-medium">
                                         {transaction.paymentProvider === EscrowPaymentProvider.STRIPE ? t("receiptDialog.stripe") : t("receiptDialog.paymentGateway")}
                                     </p>
                                </div>
                                <div>
                                    <span className="text-gray-600">{t("receiptDialog.paymentId")}</span>
                                    <p className="font-medium text-sm">
                                        {transaction.stripePaymentIntentId || "N/A"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-gray-600">{t("receiptDialog.created")}</span>
                                    <p className="font-medium">
                                        {new Date(transaction.createdAt).toLocaleString()}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-gray-600">{t("receiptDialog.updated")}</span>
                                    <p className="font-medium">
                                        {new Date(transaction.updatedAt).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        {/* Additional Information */}
                        {(transaction.contract?.id || transaction.milestone?.id || transaction.notes) && (
                            <Card className="p-6">
                                <h3 className="font-semibold text-gray-900 mb-4">{t("receiptDialog.additionalInformation")}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {transaction.contract?.id && (
                                        <div>
                                            <span className="text-gray-600">{t("receiptDialog.contractId")}</span>
                                            <p className="font-medium">{transaction.contract.id}</p>
                                        </div>
                                    )}
                                    {transaction.milestone?.id && (
                                        <div>
                                            <span className="text-gray-600">{t("receiptDialog.milestoneId")}</span>
                                            <p className="font-medium">{transaction.milestone.id}</p>
                                        </div>
                                    )}
                                    {transaction.notes && (
                                        <div className="col-span-2">
                                            <span className="text-gray-600">{t("receiptDialog.notes")}</span>
                                            <p className="font-medium">{transaction.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Footer */}
                        <div className="text-center py-6 border-t">
                            <p className="text-gray-600 mb-2">{t("receiptDialog.thankYou")}</p>
                            <p className="text-sm text-gray-500">{t("receiptDialog.automatedReceipt")}</p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="p-6 border-t bg-gray-50">
                        <div className="flex flex-col sm:flex-row gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={printReceipt}
                                className="flex items-center gap-2"
                            >
                                <Printer className="h-4 w-4" />
                                {t("receiptDialog.printReceipt")}
                            </Button>
                            <Button
                                onClick={generatePDF}
                                disabled={isGeneratingPDF}
                                className="flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                {isGeneratingPDF ? t("receiptDialog.generatingPDF") : t("receiptDialog.downloadPDF")}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ReceiptDialog;
